import { GROAS_CONTENT_SCRIPT_ID } from './constants';
import { isGroasPathRestricted } from './groasContentPath';

const GROAS_TENANT_ATTRIBUTE = 'data-hen-groas-tenant';
const GROAS_LOCATION_ATTRIBUTE = 'data-hen-groas-location';

/**
 * Records the page where GROAS executed for a path-restricted tenant. Other
 * tenants are unrestricted and do not opt into document isolation.
 */
export const recordGroasPage = (
  tenant: string | undefined,
  pageLocation: string | null,
): void => {
  if (!isGroasPathRestricted(tenant) || !pageLocation) {
    return;
  }

  document.documentElement.setAttribute(GROAS_TENANT_ATTRIBUTE, tenant);
  document.documentElement.setAttribute(GROAS_LOCATION_ATTRIBUTE, pageLocation);
};

/**
 * Returns whether a client navigation left the exact allowlisted document
 * where GROAS ran. This also catches path-mode navigation to another tenant.
 */
export const shouldReloadForGroasNavigation = (
  tenant: string | undefined,
  pageLocation: string | null,
): boolean => {
  const recordedTenant = document.documentElement.getAttribute(
    GROAS_TENANT_ATTRIBUTE,
  );
  const groasScriptExists = Boolean(
    document.getElementById(GROAS_CONTENT_SCRIPT_ID),
  );

  if (
    isGroasPathRestricted(tenant) &&
    groasScriptExists &&
    recordedTenant !== tenant
  ) {
    return true;
  }

  if (!isGroasPathRestricted(recordedTenant)) {
    return false;
  }

  return (
    tenant !== recordedTenant ||
    pageLocation !==
      document.documentElement.getAttribute(GROAS_LOCATION_ATTRIBUTE)
  );
};

/**
 * Starts a clean document after leaving a GROAS-enabled page. The vendor does
 * not expose a teardown API, so removing its script tag cannot undo code that
 * already ran or restore content it changed.
 */
export const reloadWithoutGroas = (
  reload: () => void = () => window.location.reload(),
): void => {
  reload();
};
